/**
 * badgeCase — the Badge Case tab of the Living Dex: a cozy 8-slot trophy shelf that
 * reads QuestState.badges (the Badge Circuit, owner decision D17). Earned badges bloom
 * into a warm colored disc with their name; unearned slots rest as soft empty sockets —
 * NO pressure copy, no "0/8", no checklist guilt. Just a shelf that fills at your pace.
 *
 * Presentational only: it renders into a rect the owning DexScene lays out, and returns
 * focusable cells so DexScene's shared FocusRing/gridNav keyboard path lights each slot.
 * The renderer owns no rules — the badge list is the session's truth.
 */
import Phaser from 'phaser';
import { COLORS, CSS, FONT, SPACING } from '../ui/theme.js';
import { Panel } from '../ui/Panel.js';

/** The 8 Badge-Circuit badges in earn order, with warm disc colors (display-only). */
export interface BadgeMeta {
  readonly id: string;
  readonly name: string;
  /** The disc color when earned — themed to the gym's type mood. */
  readonly color: number;
}

export const BADGE_ORDER: readonly BadgeMeta[] = [
  { id: 'meadow-badge', name: 'Meadow Badge', color: 0xc8b26a },
  { id: 'forest-badge', name: 'Rustle Badge', color: 0xafc06a },
  { id: 'lake-badge', name: 'Ripple Badge', color: 0x6aa6e8 },
  { id: 'cavern-badge', name: 'Echo Badge', color: 0xc2b48a },
  { id: 'deep-forest-badge', name: 'Verdant Badge', color: 0x84c98a },
  { id: 'mountain-badge', name: 'Summit Badge', color: 0xcf7c72 },
  { id: 'ruins-badge', name: 'Relic Badge', color: 0xb58ad0 },
  { id: 'ashland-badge', name: 'Ember Badge', color: 0xe8896a },
];

/** A focusable slot cell (for the shared FocusRing keyboard nav). */
export interface BadgeCell {
  readonly container: Phaser.GameObjects.Container;
  readonly cx: number;
  readonly cy: number;
  readonly width: number;
  readonly height: number;
}

export interface BadgeCaseResult {
  readonly container: Phaser.GameObjects.Container;
  readonly cells: readonly BadgeCell[];
  readonly cols: number;
}

export interface BadgeCaseOptions {
  /** Content rect (already inside the header chrome), centered layout. */
  readonly cx: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
  readonly badges: readonly string[];
}

/**
 * Build the Badge Case shelf. Returns a scene-level container (positioned absolutely)
 * plus the per-slot cells for keyboard focus. Caller destroys the container to tear down.
 */
export function buildBadgeCase(scene: Phaser.Scene, opts: BadgeCaseOptions): BadgeCaseResult {
  const earned = new Set(opts.badges);
  const container = scene.add.container(0, 0);

  // A warm intro that never counts or pressures.
  const heading = scene.add
    .text(opts.cx, opts.top, 'Your Badge Case', {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.md}px`,
      color: CSS.text,
      fontStyle: 'bold',
    })
    .setOrigin(0.5, 0);
  const sub = scene.add
    .text(opts.cx, opts.top + FONT.size.md + SPACING.xs, subtitle(earned.size), {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.sm}px`,
      color: CSS.textMuted,
      align: 'center',
      wordWrap: { width: Math.min(opts.width - SPACING.lg, 520) },
    })
    .setOrigin(0.5, 0);
  container.add([heading, sub]);

  const shelfTop = opts.top + FONT.size.md + FONT.size.sm + SPACING.lg;

  // Phone-portrait reachable: 2 columns on a narrow width, 4 on a roomy one.
  const cols = opts.width < 520 ? 2 : 4;
  const rows = Math.ceil(BADGE_ORDER.length / cols);
  const slot = opts.width < 520 ? 130 : 150;
  const gapX = SPACING.md;
  const gapY = SPACING.md;
  const usedW = cols * slot + (cols - 1) * gapX;
  const startX = opts.cx - usedW / 2 + slot / 2;

  const cells: BadgeCell[] = [];
  BADGE_ORDER.forEach((meta, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const x = startX + col * (slot + gapX);
    const y = shelfTop + slot / 2 + row * (slot + gapY);
    const cell = buildSlot(scene, meta, earned.has(meta.id), slot);
    cell.setPosition(x, y);
    container.add(cell);
    cells.push({ container: cell, cx: x, cy: y, width: slot, height: slot });
  });

  // Keep the shelf reference height around implicitly via row math; unused var guard.
  void rows;

  return { container, cells, cols };
}

function subtitle(count: number): string {
  if (count === 0) {
    return 'Trophies from friendly gym matches will rest here — collected whenever you like.';
  }
  if (count >= BADGE_ORDER.length) {
    return 'Every badge, side by side. What a journey — and the world keeps going.';
  }
  return 'Your badges, side by side. No rush for the rest — they’ll come.';
}

/** One shelf slot — a colored disc + name when earned, a soft socket when not. */
function buildSlot(
  scene: Phaser.Scene,
  meta: BadgeMeta,
  earned: boolean,
  size: number,
): Phaser.GameObjects.Container {
  const c = scene.add.container(0, 0);
  const panel = new Panel(scene, 0, 0, {
    width: size,
    height: size,
    fill: earned ? COLORS.surface : COLORS.surfaceAlt,
    radius: 14,
  });
  c.add(panel);

  const discY = -size / 6;
  const r = size * 0.24;
  const disc = scene.add.graphics();
  if (earned) {
    // A warm colored trophy disc with a soft rim + a gentle star gleam.
    disc.fillStyle(meta.color, 1);
    disc.fillCircle(0, discY, r);
    disc.lineStyle(3, COLORS.border, 0.85);
    disc.strokeCircle(0, discY, r);
    c.add(disc);
    const star = scene.add
      .text(0, discY, '✦', {
        fontFamily: FONT.family,
        fontSize: `${Math.round(r)}px`,
        color: '#fbfaf2',
      })
      .setOrigin(0.5);
    c.add(star);
  } else {
    // A soft empty socket — a quiet dimple, never a scolding blank.
    disc.fillStyle(COLORS.bg, 1);
    disc.fillCircle(0, discY, r);
    disc.lineStyle(2, COLORS.textFaint, 0.5);
    disc.strokeCircle(0, discY, r);
    c.add(disc);
  }

  const name = scene.add
    .text(0, size / 2 - SPACING.md, earned ? meta.name : '— — —', {
      fontFamily: FONT.family,
      fontSize: `${FONT.size.sm}px`,
      color: earned ? CSS.text : CSS.textFaint,
      fontStyle: earned ? 'bold' : 'normal',
      align: 'center',
      wordWrap: { width: size - SPACING.sm },
    })
    .setOrigin(0.5, 1);
  c.add(name);

  if (earned) {
    const tag = scene.add
      .text(0, size / 2 - SPACING.xs, 'earned', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.xs}px`,
        color: CSS.textMuted,
      })
      .setOrigin(0.5, 1);
    c.add(tag);
  }

  c.setSize(size, size);
  return c;
}
